import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Briefcase,
  Users,
  Shield,
  Settings,
  BarChart3,
  FolderOpen,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileWarning,
  Building,
  MapPin,
  Loader2,
  Bell,
  BookUser,
  Gavel,
  Lock,
  RefreshCw,
  Award,
  Calendar,
  ShieldOff,
  UserX,
  FilePlus,
  Layers,
  ListChecks,
  CalendarRange,
  FileQuestion,
} from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { useCommandStore } from '@/stores/command.store';
import { useJobs } from '@/hooks';
import type { Job } from '@coi-live/shared-types';

// Pre-configured AI search bar commands. Grouped to match the client's
// spreadsheet (Dashboard reports above the divider, Jobs-tab reports below).
const SEARCH_REPORTS = [
  // ---- Dashboard reports (items 1-8) ----
  {
    id: 'expired-coverages',
    label: 'Report with all insureds with expired coverages',
    icon: Clock,
    group: 'dashboard' as const,
  },
  {
    id: 'all-jobs-tabs',
    label: 'Report with all jobs, compliant and non-compliant (separate tabs)',
    icon: BarChart3,
    group: 'dashboard' as const,
  },
  {
    id: 'non-compliant-jobs',
    label: 'Report for all non-compliant jobs',
    icon: AlertTriangle,
    group: 'dashboard' as const,
  },
  {
    id: 'compliant-jobs',
    label: 'Report for all compliant jobs',
    icon: CheckCircle,
    group: 'dashboard' as const,
  },
  {
    id: 'jobs-by-status',
    label: 'Report for all jobs / open jobs / closed jobs (3 tabs)',
    icon: ListChecks,
    group: 'dashboard' as const,
  },
  {
    id: 'all-jobs',
    label: 'Report for all jobs',
    icon: Briefcase,
    group: 'dashboard' as const,
  },
  {
    id: 'missing-additional-insured',
    label: 'Report for all jobs missing additional insured',
    icon: FileWarning,
    group: 'dashboard' as const,
  },
  {
    id: 'incorrect-cert-holder-name',
    label: 'Report for all jobs with incorrect Certificate Holder Name',
    icon: Building,
    group: 'dashboard' as const,
  },
  {
    id: 'incorrect-cert-holder-address',
    label: 'Report for all jobs with incorrect Certificate Holder Address',
    icon: MapPin,
    group: 'dashboard' as const,
  },
  {
    id: 'expiring-week',
    label: 'Report showing all coverages expiring within the week',
    icon: Clock,
    group: 'dashboard' as const,
  },
  {
    id: 'jobs-missing-coi',
    label: 'Report for jobs missing a COI (no certificate uploaded yet)',
    icon: FileQuestion,
    group: 'dashboard' as const,
  },
  // ---- Jobs-tab reports (items 9-18) ----
  {
    id: 'procurement-jobs',
    label: 'Report showing all jobs for procurement',
    icon: Gavel,
    group: 'jobs' as const,
  },
  {
    id: 'procurement-pre-award',
    label: "Report showing procurement's pre-award cone of silence jobs",
    icon: Lock,
    group: 'jobs' as const,
  },
  {
    id: 'procurement-renewal',
    label: 'Report with procurement jobs in the renewal period',
    icon: RefreshCw,
    group: 'jobs' as const,
  },
  {
    id: 'procurement-awarded-original',
    label: "Report with procurement's awarded jobs (original term)",
    icon: Award,
    group: 'jobs' as const,
  },
  {
    id: 'original-term-jobs',
    label: 'Report with jobs in original term',
    icon: Calendar,
    group: 'jobs' as const,
  },
  {
    id: 'renewal-period-jobs',
    label: 'Report for jobs in a renewal period',
    icon: CalendarRange,
    group: 'jobs' as const,
  },
  {
    id: 'jobs-no-requirements',
    label: 'Report for jobs with no risk requirements assigned',
    icon: ShieldOff,
    group: 'jobs' as const,
  },
  {
    id: 'jobs-no-insured',
    label: 'Report for jobs with no insured assigned',
    icon: UserX,
    group: 'jobs' as const,
  },
  {
    id: 'amendment-jobs',
    label: 'Report for jobs with an amendment',
    icon: FilePlus,
    group: 'jobs' as const,
  },
  {
    id: 'insureds-multiple-jobs',
    label: 'Report for insureds assigned to more than one job',
    icon: Layers,
    group: 'jobs' as const,
  },
];

// Navigation items
const NAVIGATION_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', path: '/app', icon: BarChart3 },
  { id: 'jobs', label: 'Jobs', path: '/app/jobs', icon: Briefcase },
  { id: 'insureds', label: 'Insureds', path: '/app/insureds', icon: Users },
  { id: 'risk-reqs', label: 'Insurance Requirements', path: '/app/risk-requirements', icon: Shield },
  { id: 'directory', label: 'Directory', path: '/app/directory', icon: BookUser },
  { id: 'notifications', label: 'Notices Sent', path: '/app/notifications', icon: Bell },
  { id: 'reports', label: 'Reports', path: '/app/reports', icon: FileText },
  { id: 'settings', label: 'Settings', path: '/app/settings', icon: Settings },
];

export function CommandPalette() {
  const navigate = useNavigate();
  const { isOpen, setOpen, searchQuery, setSearchQuery } = useCommandStore();

  // Fetch projects for search - only when palette is open
  const { data: jobsData, isLoading: jobsLoading } = useJobs(
    searchQuery ? { limit: 20, search: searchQuery } : { limit: 20 }
  );

  const jobs = jobsData?.items ?? [];

  // Handle keyboard shortcut (Cmd+K / Ctrl+K)
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(!isOpen);
      }
    },
    [isOpen, setOpen]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleNavigate = (path: string) => {
    navigate(path);
    setOpen(false);
    setSearchQuery('');
  };

  const handleReportSelect = (reportId: string) => {
    navigate(`/app/reports/${reportId}`);
    setOpen(false);
    setSearchQuery('');
  };

  const handleJobSelect = (jobId: string) => {
    navigate(`/app/job/${jobId}`);
    setOpen(false);
    setSearchQuery('');
  };

  return (
    <CommandDialog open={isOpen} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Search jobs, reports, or navigate..."
        value={searchQuery}
        onValueChange={setSearchQuery}
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {/* Navigation */}
        <CommandGroup heading="Navigation">
          {NAVIGATION_ITEMS.map((item) => (
            <CommandItem
              key={item.id}
              value={item.label}
              onSelect={() => handleNavigate(item.path)}
            >
              <item.icon className="mr-2 h-4 w-4" />
              <span>{item.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        {/* Reports — Dashboard group */}
        <CommandGroup heading="Reports — Dashboard">
          {SEARCH_REPORTS.filter((r) => r.group === 'dashboard').map((report) => (
            <CommandItem
              key={report.id}
              value={report.label}
              onSelect={() => handleReportSelect(report.id)}
            >
              <report.icon className="mr-2 h-4 w-4 text-coilive-blue" />
              <span>{report.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        {/* Reports — Jobs tab group */}
        <CommandGroup heading="Reports — Jobs">
          {SEARCH_REPORTS.filter((r) => r.group === 'jobs').map((report) => (
            <CommandItem
              key={report.id}
              value={report.label}
              onSelect={() => handleReportSelect(report.id)}
            >
              <report.icon className="mr-2 h-4 w-4 text-coilive-blue" />
              <span>{report.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        {/* Jobs */}
        <CommandGroup heading="Jobs">
          {jobsLoading ? (
            <CommandItem disabled>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              <span>Loading jobs...</span>
            </CommandItem>
          ) : jobs.length === 0 ? (
            <CommandItem disabled>
              <span className="text-gray-500">No jobs found</span>
            </CommandItem>
          ) : (
            jobs.map((job: Job) => (
              <CommandItem
                key={job.id}
                value={`${job.number} ${job.name}`}
                onSelect={() => handleJobSelect(job.id)}
              >
                <FolderOpen className="mr-2 h-4 w-4" />
                <span className="font-medium">{job.number}</span>
                <span className="ml-2 text-gray-500">{job.name}</span>
              </CommandItem>
            ))
          )}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
